import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

// FormData and Blob types for file uploads
declare global {
  interface FormData {
    append(name: string, value: Blob | string, fileName?: string): void
  }
  interface Blob {
    readonly size: number
    readonly type: string
    slice(): Blob
  }
}

export interface User {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string
  profileImageUrl?: string
  subscriptionTier: 'free' | 'premium' | 'pro'
  role?: 'user' | 'admin' | 'super_admin' | 'moderator'
  createdAt: string
  updatedAt: string
}

export interface UpdateProfileRequest {
  firstName?: string
  lastName?: string
  email?: string
  username?: string
  profileImageUrl?: string
  currentPassword?: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'system'
  language?: string
  notifications?: {
    email: boolean
    push: boolean
    marketing: boolean
  }
  privacy?: {
    profileVisible: boolean
    showEmail: boolean
    showStats: boolean
  }
}

export interface UserStats {
  totalCigars: number
  totalHumidors: number
  totalSmokingSessions: number
  averageRating: number | null
  favoriteBrand: string
  joinDate: string
  lastActive: string
  lastSession: string | null
}

export interface UserApiResponse<T = unknown> {
  success: boolean
  message: string
  data: T
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/users',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['User', 'Stats'],
  endpoints: (builder) => ({
    // Get user profile
    getProfile: builder.query<User, void>({
      query: () => '/profile',
      transformResponse: (response: UserApiResponse<{ user: User }>) => response.data.user,
      providesTags: ['User'],
    }),

    // Update user profile
    updateProfile: builder.mutation<User, UpdateProfileRequest>({
      query: (updates) => ({
        url: '/profile',
        method: 'PUT',
        body: updates,
      }),
      transformResponse: (response: UserApiResponse<{ user: User }>) => response.data.user,
      invalidatesTags: ['User'],
    }),

    // Change password
    changePassword: builder.mutation<{ accessToken: string }, ChangePasswordRequest>({
      query: (passwords) => ({
        url: '/change-password',
        method: 'PUT',
        body: passwords,
        credentials: 'include',
      }),
      transformResponse: (response: UserApiResponse<{ accessToken: string }>) => response.data,
    }),

    // Delete account
    deleteAccount: builder.mutation<void, void>({
      query: () => ({
        url: '/account',
        method: 'DELETE',
      }),
      transformResponse: (response: UserApiResponse<void>) => response.data,
    }),

    // Get user preferences
    getPreferences: builder.query<UserPreferences, void>({
      query: () => '/preferences',
      transformResponse: (response: UserApiResponse<UserPreferences>) => response.data,
      providesTags: ['User'],
    }),

    // Update user preferences
    updatePreferences: builder.mutation<UserPreferences, UserPreferences>({
      query: (preferences) => ({
        url: '/preferences',
        method: 'PUT',
        body: preferences,
      }),
      transformResponse: (response: UserApiResponse<UserPreferences>) => response.data,
      invalidatesTags: ['User'],
    }),

    // Get user statistics
    getStats: builder.query<UserStats, void>({
      query: () => '/stats',
      transformResponse: (response: UserApiResponse<{ stats: { cigarCount: number; humidorCount: number; sessionCount: number; totalValue: number; averageRating: number | null; favoriteBrand: string | null; lastSession: string | null } }>) => {
        const s = response.data.stats
        return {
          totalCigars: s.cigarCount,
          totalHumidors: s.humidorCount,
          totalSmokingSessions: s.sessionCount,
          averageRating: s.averageRating,
          favoriteBrand: s.favoriteBrand || '',
          joinDate: '',
          lastActive: '',
          lastSession: s.lastSession,
        }
      },
      providesTags: ['Stats'],
    }),

    // Upload profile picture
    uploadProfilePicture: builder.mutation<{ url: string }, globalThis.FormData>({
      query: (formData) => ({
        url: '/upload-profile-picture',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: UserApiResponse<{ url: string }>) => response.data,
      invalidatesTags: ['User'],
    }),
  }),
})

export const {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useDeleteAccountMutation,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useGetStatsQuery,
  useUploadProfilePictureMutation,
} = userApi
